import { createFileRoute } from "@tanstack/react-router";

import { z } from "zod";
import { toast } from "sonner";

import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "motion/react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons/google";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { AuthApiFactory } from "@/apis/auth";
import { unwrapApiError } from "@/lib/error";
import { wait } from "@/lib/utils";
import { trackLead } from "@/events/analytics";

type ILoginSchema = z.infer<typeof LoginSchema>;

export const Route = createFileRoute("/auth/_layout/")({
  component: RouteComponent,
});

const LoginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

const UnpaidSchema = z.object({
  message: z.string().email(),
  error_code: z.literal("USER_NOT_PAID"),
});

const LoginAnimation = {
  initial: {
    opacity: 0,
    x: 50,
  },
  animate: {
    opacity: 1,
    x: 0,
  },
};

const ButtonAnimation = {
  initial: {
    opacity: 0,
    y: -25,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: 25,
  },
  transition: {
    type: "spring",
    duration: 0.3,
    bounce: 0,
  },
};

const ButtonState = {
  pending: (
    <div className="flex items-center justify-center h-full w-full bg-primary text-primary-foreground">
      <div className="spinner size-4" />
    </div>
  ),
  idle: <div className="flex items-center justify-center h-full w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">Login with Magic Link</div>,
  success: <div className="flex items-center justify-center h-full w-full bg-success text-success-foreground hover:bg-success/90 transition-colors">Magic Link sent. Please check your inbox.</div>,
  error: <div className="flex items-center justify-center h-full w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors">Failed to send Magic Link</div>,
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const [status, setStatus] = useState<"idle" | "pending" | "success" | "error">("idle");

  const form = useForm<ILoginSchema>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    if (params.email) {
      form.setValue("email", params.email);
    }
  }, [params.email, form]);

  const handleContinueWithEmail = async (data: ILoginSchema) => {
    try {
      setStatus("pending");
      const [, response] = await Promise.all([wait(2000), AuthApiFactory.Apis.SendMagicLink({ email: data.email, origin: window.location.origin })]);
      if (response.is_new_user) trackLead(data.email);

      setStatus("success");
      setTimeout(() => setStatus("idle"), 5000);
    } catch (error) {
      const unpaid = UnpaidSchema.safeParse(error);
      if (unpaid.success) {
        navigate({ to: ".", search: { plans: true, email: unpaid.data.message } });
      } else {
        toast.error("Failed to continue with Email", { description: unwrapApiError(error) });
      }
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    }
  };

  const handleContinueWithGoogle = async () => {
    setStatus("pending");
    try {
      const redirectUri = window.location.origin + "/auth/callback/google";
      const authorization = await AuthApiFactory.Apis.AuthorizeWithGoogle(redirectUri);
      window.location.href = authorization.data.url;
    } catch (error) {
      const unpaid = UnpaidSchema.safeParse(error);
      if (unpaid.success) {
        navigate({ to: ".", search: { plans: true, email: unpaid.data.message } });
      } else {
        toast.error("Failed to continue with Google", { description: unwrapApiError(error) });
      }
    } finally {
      setStatus("idle");
    }
  };

  return (
    <Fragment>
      <motion.div {...LoginAnimation} className="h-full w-full">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Login to your account</h1>
          <p className="text-xs font-medium text-muted-foreground leading-relaxed">
            By &quot;Logging In&quot;, you agree to the&nbsp;
            <Link className="underline" to="/">
              Terms of Service
            </Link>
            &nbsp; and acknowledge Zocket&apos;s&nbsp;
            <Link className="underline" to="/">
              Privacy Policy
            </Link>
          </p>
        </div>
        <div className="flex flex-col mt-10">
          <Button type="button" variant="outline" className="w-full" onClick={handleContinueWithGoogle} disabled={status === "pending"}>
            <GoogleIcon />
            Continue with Google
          </Button>
        </div>
        <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border mb-6 mt-7">
          <span className="relative z-10 bg-background px-2 text-muted-foreground">or continue with</span>
        </div>
        <Form {...form}>
          <div className="flex flex-col">
            <form onSubmit={form.handleSubmit(handleContinueWithEmail)} className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="johndoe@example.com" {...field} />
                    </FormControl>
                    <FormMessage>Enter your registered email address</FormMessage>
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full p-0 overflow-hidden relative" disabled={status === "pending"}>
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span key={status} {...ButtonAnimation} className="h-full w-full">
                    {ButtonState[status]}
                  </motion.span>
                </AnimatePresence>
              </Button>
            </form>
          </div>
        </Form>
      </motion.div>
    </Fragment>
  );
}
